import React, { useMemo, useState } from "react";
import styleLegacy from "frontend/canvas-designer-new/elements-toolbar/widgets/integration-item-color-picker.module.css";
import styleCIM from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/widgets/integration-item-color-picker.module.css";
import { DropdownButton } from "frontend/canvas-designer-new/elements-toolbar/toolbar-buttons";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { getTextColorForBackgroundColor, replaceColorOpacity } from "frontend/utils/color-utils";
import { getSelectedStatus } from "frontend/utils/monday-utils";
import { SolidColor } from "frontend/ui-components/color-opacity-picker";
import { MondayTaskElement } from "shared/datamodel/schemas/monday-task";
import { userAtom } from "state-atoms/users-atoms";
import { useSubscribe } from "replicache-react";
import { getCustomColorsForUser } from "shared/datamodel/account-mutators";
import { useAtomValue } from "jotai";
import { accountRepsAtom } from "frontend/hooks/use-account-reps";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { ColorPickerPopup } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/widgets/color-picker";
import consts from "shared/consts";

type ColorPickerTabs = "color" | "syncedColor";

export function MondayTaskColorPicker({
  elements,
  documentId,
  onChange,
  multi = false,
}: {
  elements: MondayTaskElement[];
  documentId?: string;
  onChange?: (value: { fill: string | null; colorBy: string | null }, addUndo: boolean) => void;
  multi?: boolean;
}) {
  const element = elements[0]; // First of the monday tasks
  const allSameIntegration = useMemo(() => {
    return elements.every((e) => e.integrationId === element.integrationId);
  }, [elements, element.integrationId]);

  const { fill, colorBy, integrationId, mondayItemId } = element;
  const user = useAtomValue(userAtom);
  const accountReps = useAtomValue(accountRepsAtom);
  const customColors = useSubscribe(accountReps, async (tx) => getCustomColorsForUser(tx, user?.id), []);

  const [selectedTab, setSelectedTab] = useState<ColorPickerTabs>(!multi && colorBy ? "syncedColor" : "color");
  const { getBoardIntegrationConfig, getItem } = useBoardTasks(documentId);
  const columns = getBoardIntegrationConfig(integrationId)?.columns;

  const isCIM = useIsCIMReorderEnabled();
  const newFloatersStyle = useFeatureFlag("cim-new-floaters-style");
  const newStyle = isCIM && newFloatersStyle;
  const style = newStyle ? styleCIM : styleLegacy;

  function renderPicker() {
    return (
      <div className={style.popup}>
        {allSameIntegration && (
          <div className={style.tabContainer}>
            {renderTab("Colors", "color")}
            {renderTab("Color by", "syncedColor")}
          </div>
        )}
        <div className={style.inputContainer}>
          {selectedTab === "color" && renderColorPalette()}
          {selectedTab === "syncedColor" && renderSyncedColorPicker()}
        </div>
      </div>
    );
  }

  function renderTab(label: string, tabId: ColorPickerTabs) {
    const isSelected = selectedTab === tabId;
    return (
      <div className={isSelected ? style.tabSelected : style.tab} onClick={() => setSelectedTab(tabId)}>
        {label}
      </div>
    );
  }

  function renderSyncedColorPicker() {
    const statusColumns = columns?.filter((col) => col.type === "status") ?? [];

    if (statusColumns.length === 0) {
      return (
        <div className={style.flexContainer}>
          <div className={style.syncedPickerErrorPage}>
            <span>No status columns found in board</span>
          </div>
        </div>
      );
    }

    const hasSelection = elements.length === 1 || elements.every((e) => e.colorBy === elements[0].colorBy);

    return (
      <div className={style.syncedColorPicker}>
        <span>Auto sync the color of the selected column with this task</span>
        <div className={style.divider} />
        <div className={style.syncedColorPickerGrid}>
          {statusColumns.map((column) => {
            const isSelected = hasSelection && colorBy === column.id;

            return (
              <div
                key={column.id}
                className={style.syncedColorPickerItem}
                onClick={() => onChange?.({ fill: null, colorBy: column.id }, true)}
              >
                <div className={isSelected ? style.radioButtonSelected : style.radioButton}>
                  {isSelected && <div className={style.radioButtonInnerCircle} />}
                </div>
                <span>{column.title}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderColorPalette() {
    return (
      <ColorPickerPopup
        value={fill}
        onChange={handleColorSelection}
        withTransparency={true}
        allowCustomColor={true}
        customColors={customColors}
        colorPalette={consts.COLOR_PALETTE_INCLUDING_WHITE_TRANSPARENT}
        closePopup={function (): void {
          throw new Error("Function not implemented.");
        }}
      />
    );
  }

  function renderSyncedIcon(color: string = "#113357") {
    return (
      <svg width="15" height="15" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.9703 6.50548L11.8408 5.99871C11.5647 4.91785 10.954 3.9608 10.1064 3.27356C9.25897 2.58657 8.22203 2.20729 7.15591 2.1901C6.08993 2.17291 5.04976 2.51855 4.1952 3.17604C3.49954 3.71127 3.16113 4.23213 2.75087 5.07725L4.69782 4.849L4.81962 5.88797L1.13146 6.32034L0.688082 2.53824L1.72705 2.41644L1.9519 4.33438C2.34696 3.56487 2.77348 2.95001 3.55731 2.34695C4.59762 1.54655 5.86815 1.12312 7.17278 1.14415C8.47726 1.16519 9.73929 1.6293 10.7651 2.46097C11.7907 3.29237 12.5236 4.44498 12.8543 5.73979L12.9838 6.24656L11.9703 6.50548Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.95281 7.37598L2.02238 7.89437C2.17077 9.00004 2.66582 10.0217 3.42773 10.8029C4.18936 11.5839 5.17509 12.0814 6.23195 12.2226C7.28868 12.3637 8.36202 12.1416 9.28733 11.5881C10.0406 11.1375 10.4373 10.6595 10.9432 9.86795L8.98292 9.86795L8.98292 8.82186L12.6963 8.82187L12.6963 12.6299L11.6502 12.6299L11.6502 10.6988C11.1683 11.4171 10.6731 11.9781 9.82434 12.4858C8.69792 13.1596 7.38672 13.4322 6.09342 13.2594C4.80026 13.0867 3.60085 12.4788 2.67882 11.5333C1.75707 10.5881 1.16336 9.35805 0.98559 8.03352L0.916016 7.51513L1.95281 7.37598Z"
          fill={color}
        />
      </svg>
    );
  }

  function handleColorSelection(color: string | number, addUndo: boolean) {
    if (typeof color === "number") {
      color = replaceColorOpacity(fill, color);
    }
    onChange?.({ fill: color, colorBy: null }, addUndo);
  }

  function getItemColor(itemId: string, colId: string) {
    const item = getItem(itemId, integrationId);
    const status = getSelectedStatus(item?.columnValues, { id: colId });
    return status.color;
  }

  const gradient = "conic-gradient(#ff4800, #dfd902, #20dc68, #0092f4, #da54d8, #ff4800)";
  let iconColor: string = fill ?? "transparent";
  let syncIconColor: string = "#113357";

  if (multi) {
    iconColor = gradient;
  } else if (colorBy) {
    iconColor = mondayItemId ? getItemColor(mondayItemId, colorBy) : gradient;
    syncIconColor = getTextColorForBackgroundColor(iconColor);
  }

  return (
    <DropdownButton
      tooltip="Color"
      renderPopup={() => renderPicker()}
      data-testid="MondayTaskColorPicker"
      className={style.container}
    >
      <div className={style.dropdownIcon}>
        <SolidColor color={iconColor} />
        {!multi && colorBy && <div className={style.syncIcon}>{renderSyncedIcon(syncIconColor)}</div>}
      </div>
    </DropdownButton>
  );
}
